import React from "react";

const MODEL_LABELS = {
	1: "Global Models",
	2: "Australian/New Zealand Models",
};

const HORSE_LABELS = {
	sire: "Sire",
	dam: "Dam",
	sire_sire: "Paternal grandsire",
	sire_dam: "Paternal granddam",
	dam_sire: "Maternal grandsire",
	dam_dam: "Maternal granddam",
};

function getHorseName(type, selected, selectionDetails) {
	const detail = selectionDetails?.[type];
	if (detail?.display_name || detail?.name) {
		return detail.display_name || detail.name;
	}

	const value = selected?.[type];
	const id = Array.isArray(value) ? value[0] : value;
	return id ? `Horse #${id}` : null;
}

function ReviewGroup({ title, types, selected, selectionDetails }) {
	return (
		<div className="pedigree-review-group">
			<h3>{title}</h3>
			<dl>
				{types.map((type) => {
					const name = getHorseName(type, selected, selectionDetails);
					if (!name) return null;
					return (
						<div className="pedigree-review-row" key={type}>
							<dt>{HORSE_LABELS[type]}</dt>
							<dd>{name}</dd>
						</div>
					);
				})}
			</dl>
		</div>
	);
}

function ReviewStep({
	selected,
	selectionDetails,
	cost,
	availableCredits,
	hasSession,
	isSponsored,
	isSubmitting,
	onBack,
	onEditDetails,
	onGenerate,
}) {
	const numericBalance = Number(availableCredits || 0);
	const numericCost = Number(cost || 0);
	const remainingCredits = Math.max(0, numericBalance - numericCost);
	const hasEnoughCredits = isSponsored || !hasSession || numericBalance >= numericCost;

	return (
		<div className="component-card pedigree-step-card">
			<div className="pedigree-step-card__content">
				<h2 className="subtitle is-black mb-5">Review your report</h2>
				<p className="pedigree-step-help mb-20">
					Check the details below before generating your pedigree analysis.
				</p>

				<div className="pedigree-review-layout">
					<section className="pedigree-review-panel" aria-labelledby="review-details-title">
						<div className="pedigree-review-heading">
							<h3 id="review-details-title">Report details</h3>
							<button type="button" className="pedigree-text-button" onClick={onEditDetails}>
								Edit
							</button>
						</div>
						<dl>
							<div className="pedigree-review-row">
								<dt>Name</dt>
								<dd>{selected.name}</dd>
							</div>
							<div className="pedigree-review-row">
								<dt>Model</dt>
								<dd>{MODEL_LABELS[String(selected.ml_model)] || "Global Models"}</dd>
							</div>
						</dl>
					</section>

					<section className="pedigree-review-panel" aria-labelledby="review-parents-title">
						<div className="pedigree-review-heading">
							<h3 id="review-parents-title">Selected parents</h3>
							<button type="button" className="pedigree-text-button" onClick={onBack}>
								Edit
							</button>
						</div>
						<div className="pedigree-review-family">
							<ReviewGroup
								title="Paternal line"
								types={selected.sire ? ["sire"] : ["sire_sire", "sire_dam"]}
								selected={selected}
								selectionDetails={selectionDetails}
							/>
							<ReviewGroup
								title="Maternal line"
								types={selected.dam ? ["dam"] : ["dam_sire", "dam_dam"]}
								selected={selected}
								selectionDetails={selectionDetails}
							/>
						</div>
					</section>

					<aside className="pedigree-cost-panel" aria-label="Report cost summary">
						<h3>Cost summary</h3>
						<div className="pedigree-cost-row">
							<span>Report cost</span>
							<strong>{numericCost} credits</strong>
						</div>
						{hasSession ? (
							<>
								<div className="pedigree-cost-row">
									<span>Current balance</span>
									<strong>{numericBalance} credits</strong>
								</div>
								<div className="pedigree-cost-row pedigree-cost-row--total">
									<span>Balance after report</span>
									<strong>{remainingCredits} credits</strong>
								</div>
							</>
						) : (
							<p className="field-help">Sign in to view your available balance.</p>
						)}
						{!hasEnoughCredits && (
							<p className="pedigree-inline-error" role="alert">
								You need {numericCost - numericBalance} more credits to generate this report.
							</p>
						)}
					</aside>
				</div>

				<div className="pedigree-step-actions">
					<button type="button" className="button outline" onClick={onBack} disabled={isSubmitting}>
						<i className="fas fa-arrow-left mr-10" aria-hidden="true" />
						Back to parents
					</button>
					<button
						type="button"
						className="button"
						onClick={onGenerate}
						disabled={isSubmitting}
						data-tour-id="pedigree-generate-button"
					>
						<i className="fas fa-folder-plus mr-10" aria-hidden="true" />
						{isSubmitting ? "Starting report…" : hasSession ? "Generate report" : "Sign in and generate"}
					</button>
				</div>
			</div>
		</div>
	);
}

export default ReviewStep;
